import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Clock, Lock, ArrowLeft, ChevronRight, User, Briefcase } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import SiteHeader from '../components/SiteHeader';
import SiteBackground from '../components/SiteBackground';
import api from '../utils/api';

const ContactPage = () => {
  const [companyInfo, setCompanyInfo] = useState(null);
  const [navigationItems, setNavigationItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedFooterMenu, setExpandedFooterMenu] = useState(null); // 展開中のフッターメニュー
  const [dialogType, setDialogType] = useState(null); // null | 'user' | 'recruit'
  const [offices, setOffices] = useState([]);
  const [showTourForm, setShowTourForm] = useState(false);
  const [submittingTour, setSubmittingTour] = useState(false);
  const [tourForm, setTourForm] = useState({ name: '', phone: '', email: '', office_name: '', preferred_date: '', message: '' });
  const { toast } = useToast();

  const submitTour = async () => {
    if (!tourForm.name || !tourForm.phone || !tourForm.office_name) {
      toast({ title: '入力エラー', description: 'お名前・電話番号・見学希望事業所は必須です', variant: 'destructive' });
      return;
    }
    setSubmittingTour(true);
    try {
      const selected = offices.find((o) => o.name === tourForm.office_name);
      await api.post('/tour-reservations', { ...tourForm, office_id: selected ? selected.id : null });
      toast({ title: '送信しました', description: '見学予約を受け付けました。担当者よりご連絡いたします。' });
      setShowTourForm(false);
      setDialogType(null);
      setTourForm({ name: '', phone: '', email: '', office_name: '', preferred_date: '', message: '' });
    } catch (e) {
      toast({ title: 'エラー', description: '送信に失敗しました。時間をおいて再度お試しください。', variant: 'destructive' });
    } finally {
      setSubmittingTour(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [companyResponse, navResponse, officesResponse] = await Promise.all([
          api.get('/company-info'),
          api.get('/navigation'),
          api.get('/offices/active'),
        ]);
        setCompanyInfo(companyResponse.data);
        setNavigationItems(navResponse.data);
        setOffices(officesResponse.data);
      } catch (error) {
        console.error('データの取得に失敗しました:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-white text-gray-900 flex items-center justify-center">
        <div className="text-amber-500">読み込み中...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-gray-900 relative">
      <SiteBackground />
      <SiteHeader activePath="/contact" />

      {/* コンテンツ */}
      <main className="pt-40 pb-20 relative">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center text-amber-500 hover:text-amber-500 mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            ホームに戻る
          </Link>
          
          <div className="bg-gradient-to-br from-gray-50/50 to-gray-100/50 border border-amber-500/20 rounded-2xl p-8 md:p-12 backdrop-blur-sm">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-center bg-gradient-to-r from-amber-500 to-amber-600 bg-clip-text text-transparent">
              お問い合わせ
            </h1>
            
            {/* お問い合わせ種別 */}
            <div className="mb-12">
              <p className="text-base font-bold text-gray-800 mb-4">お問い合わせの種類をお選びください</p>
              <div className="grid sm:grid-cols-2 gap-5">
                <button
                  type="button"
                  onClick={() => setDialogType('user')}
                  data-testid="inquiry-type-user"
                  className="group text-left p-6 rounded-2xl border-2 border-amber-500/40 bg-gradient-to-br from-amber-500/10 to-white shadow-lg shadow-amber-500/10 hover:shadow-xl hover:shadow-amber-500/25 hover:border-amber-500 hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center flex-shrink-0 shadow-md shadow-amber-500/30">
                      <User className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-bold text-gray-900 mb-1 group-hover:text-amber-600 transition-colors">利用者に関するお問い合わせ</h3>
                      <p className="text-sm text-gray-600">サービス内容・ご利用・ご相談について</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-amber-500 flex-shrink-0 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300" />
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setDialogType('recruit')}
                  data-testid="inquiry-type-recruit"
                  className="group text-left p-6 rounded-2xl border-2 border-amber-500/40 bg-gradient-to-br from-amber-500/10 to-white shadow-lg shadow-amber-500/10 hover:shadow-xl hover:shadow-amber-500/25 hover:border-amber-500 hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center flex-shrink-0 shadow-md shadow-amber-500/30">
                      <Briefcase className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-bold text-gray-900 mb-1 group-hover:text-amber-600 transition-colors">求人応募のお問い合わせ</h3>
                      <p className="text-sm text-gray-600">採用・求人へのご応募について</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-amber-500 flex-shrink-0 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300" />
                  </div>
                </button>
              </div>
            </div>

            {/* お問い合わせ種別ポップアップ */}
            <Dialog open={!!dialogType} onOpenChange={(open) => { if (!open) setDialogType(null); }}>
              <DialogContent className="bg-white border-amber-500/30" data-testid="inquiry-dialog">
                {dialogType === 'recruit' ? (
                  <>
                    <DialogHeader>
                      <DialogTitle className="text-gray-900">求人応募のお問い合わせ</DialogTitle>
                      <DialogDescription className="text-gray-600">
                        採用・求人へのご応募は、応募フォームよりお願いいたします。
                      </DialogDescription>
                    </DialogHeader>
                    <div className="mt-4">
                      <Link to="/apply" onClick={() => { setDialogType(null); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
                        <Button className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-gray-900" data-testid="go-apply-btn">
                          応募フォームへ
                        </Button>
                      </Link>
                    </div>
                  </>
                ) : (
                  <>
                    <DialogHeader>
                      <DialogTitle className="text-gray-900">利用者に関するお問い合わせ</DialogTitle>
                      <DialogDescription className="text-gray-600">
                        サービス内容・ご利用・ご相談は、メールまたはお電話で承ります。
                      </DialogDescription>
                    </DialogHeader>
                    {companyInfo && (
                      <div className="mt-4 space-y-3">
                        <div className="flex items-center space-x-3 text-gray-700">
                          <Phone className="w-5 h-5 text-amber-500 flex-shrink-0" />
                          <span>{companyInfo.phone}</span>
                        </div>
                        <div className="flex items-center space-x-3 text-gray-700">
                          <Mail className="w-5 h-5 text-amber-500 flex-shrink-0" />
                          <span className="break-all">{companyInfo.email}</span>
                        </div>
                        <a href={`tel:${companyInfo.phone}`} className="relative block w-full pt-2">
                          <span className="absolute left-0 right-0 top-2 bottom-0 rounded-xl bg-amber-400/50 animate-ping pointer-events-none"></span>
                          <Button className="relative w-full h-16 text-lg font-extrabold bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600 hover:from-amber-500 hover:to-amber-700 text-gray-900 shadow-2xl shadow-amber-500/50 ring-2 ring-amber-300 hover:scale-[1.03] transition-transform duration-300" data-testid="call-user-btn">
                            <Phone className="w-6 h-6 mr-2 animate-pulse" />
                            電話する
                            <span className="ml-2 tracking-wide">{companyInfo.phone}</span>
                          </Button>
                        </a>
                        <div className="grid sm:grid-cols-2 gap-2">
                          <a href={`mailto:${companyInfo.email}?subject=${encodeURIComponent('利用者に関するお問い合わせ')}`} className="w-full">
                            <Button variant="outline" className="w-full border-amber-500/40 text-amber-600 hover:bg-amber-500/10" data-testid="mailto-user-btn">
                              <Mail className="w-4 h-4 mr-2" />メールで問い合わせる
                            </Button>
                          </a>
                          <Button
                            variant="outline"
                            onClick={() => { setDialogType(null); setShowTourForm(true); }}
                            className="w-full border-amber-500/40 text-amber-600 hover:bg-amber-500/10"
                            data-testid="open-tour-form-btn"
                          >
                            見学予約フォーム
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </DialogContent>
            </Dialog>

            {/* 見学予約フォーム ポップアップ */}
            <Dialog open={showTourForm} onOpenChange={(open) => { if (!open) setShowTourForm(false); }}>
              <DialogContent className="bg-white border-amber-500/30 max-h-[90vh] overflow-y-auto" data-testid="tour-dialog">
                <DialogHeader>
                  <DialogTitle className="text-gray-900">見学予約フォーム</DialogTitle>
                  <DialogDescription className="text-gray-600">
                    ご希望の事業所と日時をお知らせください。担当者よりご連絡いたします。
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 mt-2">
                  <div className="space-y-1">
                    <Label className="text-gray-700">見学希望事業所 <span className="text-red-500">*</span></Label>
                    <Select value={tourForm.office_name} onValueChange={(v) => setTourForm({ ...tourForm, office_name: v })}>
                      <SelectTrigger data-testid="tour-office-select" className="bg-white border-amber-500/30 text-gray-900">
                        <SelectValue placeholder="事業所を選択" />
                      </SelectTrigger>
                      <SelectContent>
                        {offices.map((o) => (
                          <SelectItem key={o.id} value={o.name}>{o.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-gray-700">お名前 <span className="text-red-500">*</span></Label>
                    <Input value={tourForm.name} onChange={(e) => setTourForm({ ...tourForm, name: e.target.value })} className="bg-white border-amber-500/30 text-gray-900" data-testid="tour-name-input" />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-gray-700">電話番号 <span className="text-red-500">*</span></Label>
                      <Input value={tourForm.phone} onChange={(e) => setTourForm({ ...tourForm, phone: e.target.value })} className="bg-white border-amber-500/30 text-gray-900" data-testid="tour-phone-input" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-gray-700">メールアドレス</Label>
                      <Input value={tourForm.email} onChange={(e) => setTourForm({ ...tourForm, email: e.target.value })} className="bg-white border-amber-500/30 text-gray-900" data-testid="tour-email-input" />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-gray-700">見学希望日</Label>
                    <Input type="date" value={tourForm.preferred_date} onChange={(e) => setTourForm({ ...tourForm, preferred_date: e.target.value })} className="bg-white border-amber-500/30 text-gray-900" data-testid="tour-date-input" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-gray-700">ご要望・ご質問</Label>
                    <Textarea value={tourForm.message} onChange={(e) => setTourForm({ ...tourForm, message: e.target.value })} rows={3} className="bg-white border-amber-500/30 text-gray-900" data-testid="tour-message-input" />
                  </div>
                  <Button onClick={submitTour} disabled={submittingTour} className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-gray-900" data-testid="tour-submit-btn">
                    {submittingTour ? '送信中...' : '見学を予約する'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {companyInfo && (
              <div className="grid md:grid-cols-2 gap-6">
                {/* 住所 */}
                <Card className="bg-gradient-to-br from-gray-50/50 to-gray-100/50 border-amber-500/30">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-6 h-6 text-gray-900" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">所在地</h3>
                        <p className="text-gray-600">{companyInfo.address}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* 電話 */}
                <Card className="bg-gradient-to-br from-gray-50/50 to-gray-100/50 border-amber-500/30">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Phone className="w-6 h-6 text-gray-900" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">電話・FAX</h3>
                        <p className="text-gray-600 mb-1">TEL: {companyInfo.phone}</p>
                        <p className="text-gray-600">FAX: {companyInfo.fax}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* メール */}
                <Card className="bg-gradient-to-br from-gray-50/50 to-gray-100/50 border-amber-500/30">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Mail className="w-6 h-6 text-gray-900" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">メールアドレス</h3>
                        <a 
                          href={`mailto:${companyInfo.email}`}
                          className="text-amber-500 hover:text-amber-500 transition-colors"
                        >
                          {companyInfo.email}
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* 営業時間 */}
                <Card className="bg-gradient-to-br from-gray-50/50 to-gray-100/50 border-amber-500/30">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Clock className="w-6 h-6 text-gray-900" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">営業時間</h3>
                        <p className="text-gray-600">{companyInfo.business_hours}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* 地図プレースホルダー */}
            <div className="mt-12">
              <h2 className="text-2xl font-bold mb-6 text-gray-900">アクセス</h2>
              <div className="bg-white/50 border border-amber-500/20 rounded-xl p-8 text-center">
                <MapPin className="w-16 h-16 text-amber-500 mx-auto mb-4" />
                <p className="text-gray-600">
                  {companyInfo?.address}
                </p>
              </div>
            </div>

            {/* お問い合わせ注意事項 */}
            <div className="mt-12 p-6 bg-amber-500/5 border border-amber-500/20 rounded-xl">
              <h3 className="text-lg font-semibold text-amber-500 mb-3">お問い合わせについて</h3>
              <ul className="space-y-2 text-gray-600 text-sm">
                <li>• お電話でのお問い合わせは営業時間内にお願いいたします</li>
                <li>• メールでのお問い合わせは24時間受け付けております</li>
                <li>• 回答までに数日いただく場合がございます</li>
              </ul>
            </div>
          </div>
        </div>
      </main>

      {/* フッター */}
      <footer className="relative border-t border-amber-500/20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid md:grid-cols-3 gap-12 mb-12">
            {/* お問い合わせ */}
            <div>
              <h3 className="text-xl font-bold mb-6 bg-gradient-to-r from-amber-500 to-amber-600 bg-clip-text text-transparent">
                お問い合わせ
              </h3>
              {companyInfo ? (
                <div className="space-y-4">
                  <div className="flex items-start space-x-3 text-gray-600 hover:text-amber-500 transition-colors">
                    <MapPin className="w-5 h-5 mt-1 flex-shrink-0 text-amber-500" />
                    <span>{companyInfo.address}</span>
                  </div>
                  <div className="flex items-center space-x-3 text-gray-600 hover:text-amber-500 transition-colors">
                    <Phone className="w-5 h-5 flex-shrink-0 text-amber-500" />
                    <span>{companyInfo.phone} {companyInfo.fax && `FAX ${companyInfo.fax}`}</span>
                  </div>
                  <div className="flex items-center space-x-3 text-gray-600 hover:text-amber-500 transition-colors">
                    <Mail className="w-5 h-5 flex-shrink-0 text-amber-500" />
                    <span>{companyInfo.email}</span>
                  </div>
                  <div className="flex items-center space-x-3 text-gray-600 hover:text-amber-500 transition-colors">
                    <Clock className="w-5 h-5 flex-shrink-0 text-amber-500" />
                    <span>{companyInfo.business_hours}</span>
                  </div>
                </div>
              ) : (
                <p className="text-gray-500">読み込み中...</p>
              )}
            </div>

            {/* メニュー */}
            <div>
              <h3 className="text-xl font-bold mb-6 bg-gradient-to-r from-amber-500 to-amber-600 bg-clip-text text-transparent">
                メニュー
              </h3>
              <ul className="space-y-3">
                {navigationItems
                  .filter(item => !item.parent_id)
                  .map((item) => {
                    const subItems = navigationItems.filter(sub => sub.parent_id === item.id);
                    const hasSubMenu = subItems.length > 0;
                    const isExpanded = expandedFooterMenu === item.id;
                    
                    return (
                      <li key={item.id}>
                        {hasSubMenu ? (
                          // サブメニューがある場合：クリックで展開
                          <button
                            onClick={() => setExpandedFooterMenu(isExpanded ? null : item.id)}
                            className="text-gray-600 hover:text-amber-500 transition-colors duration-300 flex items-center group w-full text-left"
                          >
                            <ChevronRight className={`w-4 h-4 mr-2 transition-transform duration-300 ${isExpanded ? 'rotate-90' : ''}`} />
                            {item.label}
                          </button>
                        ) : (
                          // サブメニューがない場合：通常のリンク
                          <Link
                            to={item.path}
                            className="text-gray-600 hover:text-amber-500 transition-colors duration-300 flex items-center group"
                          >
                            <ChevronRight className="w-4 h-4 mr-2 group-hover:translate-x-1 transition-transform" />
                            {item.label}
                          </Link>
                        )}
                        
                        {/* サブメニュー */}
                        {hasSubMenu && isExpanded && (
                          <ul className="ml-6 mt-2 space-y-2 animate-in fade-in slide-in-from-top-2 duration-300">
                            {subItems.map((subItem) => (
                              <li key={subItem.id}>
                                <Link
                                  to={subItem.path}
                                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                                  className="text-gray-500 hover:text-amber-500 transition-colors duration-300 flex items-center group text-sm"
                                >
                                  <ChevronRight className="w-3 h-3 mr-2 group-hover:translate-x-1 transition-transform" />
                                  {subItem.label}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    );
                  })}
              </ul>
            </div>

            {/* 最新情報 */}
            <div>
              <h3 className="text-xl font-bold mb-6 bg-gradient-to-r from-amber-500 to-amber-600 bg-clip-text text-transparent">
                最新情報
              </h3>
              <p className="text-gray-600 text-sm">
                最新のニュースやお知らせは<Link to="/" className="text-amber-500 hover:text-amber-500 underline">ホームページ</Link>をご覧ください。
              </p>
            </div>
          </div>

          {/* コピーライトと管理者ログイン */}
          <div className="border-t border-amber-500/20 pt-8">
            <div className="flex justify-between items-center">
              <p className="text-gray-600 text-sm">
                Copyright © 2002 {companyInfo?.name}
              </p>
              <Link to="/admin/login">
                <Button 
                  variant="ghost" 
                  className="text-gray-500 hover:text-amber-500 text-sm"
                >
                  <Lock className="w-4 h-4 mr-2" />
                  管理者ログイン
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ContactPage;
